// Global Instructions Rule Applied!
import {
  FieldTypeEnum,
  SelectTypeEnum,
  FieldConfig,
  CompanyFieldConfig,
  SelectFieldConfig,
  NumberFieldConfig,
  TextFieldConfig
} from '../../../core/components/InputManager/types.ts'
import { introducedByHelp, leadHelp, SOURCED_BY_HELP } from '../../../core/utils/teamContactsHelp.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import {
  TABLE_CONTACTS,
  TABLE_CONTACT_ACCOUNTS,
  TABLE_CONTACT_DEALS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG,
  FIELD_KEY_ACTION,
  ACTION_TYPE_UPSERT,
  ACTION_TYPE_DELETE,
  RELATIONSHIP_CATEGORY_LEAD
} from '../../../state_manager/globals.ts'
import { ContactsModelFields } from '../../../state_manager/data_models/contacts_model.ts'
import { ContactAccountsModelFields } from '../../../state_manager/data_models/contact_accounts_model.ts'
import { ContactDealsModelFields } from '../../../state_manager/data_models/contact_deals_model.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../state_manager/constants/strings.ts'
import {
  LOOKUP_CATEGORY_INDUSTRY,
  LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA,
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_PIPELINE_STAGE,
  LOOKUP_CATEGORY_SECURITY_TYPE,
  LOOKUP_CATEGORY_DEAL_TYPE
} from '../../../state_manager/constants/lookupCategories.ts'
import {
  buildCriticalTechnologyAreaField,
  CRITICAL_TECHNOLOGY_AREA_COLUMN
} from '../../global_configs/criticalTechnologyAreas.tsx'

/**
 * Field configuration utilities for Deal Drawer InputManager integration
 * Generates InputManager field configurations for different sections of the drawer
 */

/**
 * Generates field configurations for Deal Information section
 * @param {Object} formData - Current form data
 * @param {Object} helpers - Helper functions and data
 * @returns {Array} Array of FieldConfig objects
 */
export function getDealInfoFields(formData: any, helpers: any): FieldConfig[] {
  const { getCurrencyById, getPipelineStageOptions, getCompanyDetailsById } = helpers

  const fields: FieldConfig[] = [
    // Deal Name
    {
      key: DealFields.deals__name,
      label: 'Name',
      type: FieldTypeEnum.TEXT,
      value: formData[DealFields.deals__name] || '',
      required: true,
      editable: true,
      autoFocus: true
    },
    // New Company Checkbox
    {
      key: DealFields.prog__is_new_account,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value: formData[DealFields.prog__is_new_account] || false,
      checkboxLabel: 'New Company?',
      editable: true
    }
  ]

  // Company field - conditional based on new_company toggle
  if (!formData[DealFields.prog__is_new_account]) {
    fields.push({
      key: DealFields.deals__account,
      label: 'Company',
      type: FieldTypeEnum.COMPANY,
      value: formData[DealFields.deals__account] || '',
      required: true,
      editable: true,
      getCompanyDetailsById
    } as CompanyFieldConfig)
  }

  // Currency
  fields.push({
    key: DealFields.deals__currency,
    label: 'Currency',
    type: FieldTypeEnum.SELECT,
    value: formData[DealFields.deals__currency] || '',
    required: true,
    editable: true,
    selectType: SelectTypeEnum.FILTER,
    options: helpers.getCurrencyOptions ? helpers.getCurrencyOptions() : [],
    allowClear: true
  } as SelectFieldConfig)

  // Deal Type — drives execution-pipeline routing (cfg.deal_execution_pipeline_map)
  fields.push({
    key: DealFields.deals__deal_type,
    label: 'Deal Type',
    type: FieldTypeEnum.SELECT,
    value: formData[DealFields.deals__deal_type] || '',
    required: true,
    editable: true,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_DEAL_TYPE,
    allowClear: false
  } as SelectFieldConfig)

  // Allocation Available sits under Raising in Round Information (owner
  // instruction 2026-09-16). That section only renders for a funding round, so
  // a non-funding-round deal keeps the field here instead of losing it.
  if (!isNewDealFundingRound(formData)) {
    fields.push(buildNewDealAllocationField(formData, getCurrencyById))
  }

  // Pipeline Stage
  // Note: getPipelineStageOptions helper must provide options filtered by visible_tbl = true
  // from pipeline_configs (not pipeline_stages directly)
  //
  // Omitted entirely for a deal type excluded from Deal Origination
  // (cfg.pipeline_deal_type_exclusions): such a deal never joins the
  // origination board, so offering a stage would promise something the backend
  // discards. Execution routing is unaffected and needs no field here.
  if (!helpers.isOriginationExcluded?.(formData[DealFields.deals__deal_type])) {
    fields.push({
      key: DealFields.pipeline_deals__stage,
      label: 'Pipeline Stage',
      type: FieldTypeEnum.SELECT,
      value: formData[DealFields.pipeline_deals__stage] || '',
      editable: true,
      selectType: SelectTypeEnum.FILTER,
      options: getPipelineStageOptions ? getPipelineStageOptions() : [],
      allowClear: true
    } as SelectFieldConfig)
  }

  // Is Funding Round Checkbox - after Pipeline Stage
  fields.push({
    key: DealFields.rounds__is_funding_round,
    type: FieldTypeEnum.CHECKBOX,
    label: '',
    value:
      formData[DealFields.rounds__is_funding_round] !== undefined
        ? formData[DealFields.rounds__is_funding_round]
        : true,
    checkboxLabel: 'Is Funding Round',
    editable: true
  })

  return fields
}

/**
 * Generates field configurations for Round Information section
 * @param {Object} formData - Current form data
 * @param {Object} helpers - Helper functions and data
 * @returns {Array} Array of FieldConfig objects
 */
export function getRoundInfoFields(formData: any, helpers: any): FieldConfig[] {
  const { getCurrencyById } = helpers
  const roundId = formData[DealFields.rounds__id] || formData.round || null
  const currencySymbol = getCurrencyById(formData[DealFields.deals__currency])?.symbol || '$'

  // Order is the owner's (2026-09-16): Round Name, Funding Stage, Security
  // Type, Raising, Allocation Available, Pre-money, Post-money.
  return [
    // Round Name
    {
      key: DealFields.rounds__name,
      label: 'Round Name',
      type: FieldTypeEnum.TEXT,
      value: formData[DealFields.rounds__name] || '',
      editable: !!roundId
    },

    // Funding Stage
    {
      key: DealFields.rounds__funding_stage,
      label: 'Funding Stage',
      type: FieldTypeEnum.SELECT,
      value: formData[DealFields.rounds__funding_stage] || '',
      editable: !!roundId,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE,
      allowClear: true
    },

    // Security Type
    {
      key: DealFields.rounds__security_type,
      label: 'Security Type',
      type: FieldTypeEnum.SELECT,
      value: formData[DealFields.rounds__security_type] || '',
      editable: !!roundId,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE,
      allowClear: true
    },

    // Raising Amount
    {
      key: DealFields.rounds__raise_amount,
      label: 'Raising',
      type: FieldTypeEnum.CURRENCY,
      value: formData[DealFields.rounds__raise_amount] || '',
      editable: !!roundId,
      currencySymbol,
      min: 0,
      step: 0.01
    },

    // Allocation Available — a deals column, editable regardless of the round
    buildNewDealAllocationField(formData, getCurrencyById),

    // Pre-money Valuation
    {
      key: DealFields.rounds__pre_money_valuation,
      label: 'Pre-money Valuation',
      type: FieldTypeEnum.CURRENCY,
      value: formData[DealFields.rounds__pre_money_valuation] || '',
      editable: !!roundId,
      currencySymbol,
      min: 0,
      step: 0.01
    },

    // Post-money Valuation
    {
      key: DealFields.rounds__post_money_valuation,
      label: 'Post-money Valuation',
      type: FieldTypeEnum.CURRENCY,
      value: formData[DealFields.rounds__post_money_valuation] || '',
      editable: !!roundId,
      currencySymbol,
      min: 0,
      step: 0.01
    }
  ]
}

/** The New Deal drawer's "Is Funding Round" default is true until the box is touched. */
export function isNewDealFundingRound(formData: any): boolean {
  return formData?.[DealFields.rounds__is_funding_round] !== undefined
    ? !!formData[DealFields.rounds__is_funding_round]
    : true
}

function buildNewDealAllocationField(formData: any, getCurrencyById: any): NumberFieldConfig {
  return {
    key: DealFields.deals__allocation_available,
    label: 'Allocation Available',
    type: FieldTypeEnum.CURRENCY,
    value: formData[DealFields.deals__allocation_available] || '',
    editable: true,
    currencySymbol: getCurrencyById(formData[DealFields.deals__currency])?.symbol || '$',
    min: 0,
    step: 0.01
  } as NumberFieldConfig
}

/**
 * Generates field configurations for New Company Information section
 * @param {Object} formData - Current form data
 * @param {Object} helpers - Helper functions and data
 * @returns {Array} Array of FieldConfig objects
 */
export function getNewCompanyFields(formData: any, helpers: any): FieldConfig[] {
  return [
    // Company Name
    {
      key: DealFields.accounts__name,
      label: 'Company',
      type: FieldTypeEnum.TEXT,
      value: formData[DealFields.accounts__name] || formData[DealFields.deals__account] || '',
      required: true,
      editable: true,
      autoFocus: false
    },

    // Industry
    {
      key: DealFields.accounts__industry,
      label: 'Industry',
      type: FieldTypeEnum.SELECT,
      value: formData[DealFields.accounts__industry] || '',
      editable: true,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_INDUSTRY,
      allowClear: true,
      autoFocus: false
    },

    // Critical Technology Area — immediately below Industry.
    buildCriticalTechnologyAreaField({
      options: helpers?.getLookupOptions
        ? helpers.getLookupOptions(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA)
        : [],
      value: formData[DealFields.accounts__critical_technology_area],
      key: DealFields.accounts__critical_technology_area
    }),

    // Website
    {
      key: DealFields.accounts__website,
      label: 'Website',
      type: FieldTypeEnum.URL,
      value: formData[DealFields.accounts__website] || '',
      editable: true,
      autoFocus: false
    },

    // City
    {
      key: DealFields.accounts__business_city_code,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: formData[DealFields.accounts__business_city_code] || '',
      editable: true,
      country: formData[DealFields.accounts__business_country_code],
      autoFocus: false,
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'cityName', targetField: 'business_city_label' },
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject) => {
        // Handle clearing: when cityObject is empty string/null/undefined, set values to null explicitly
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        // Extract values from CitySelect object: { label, value, name, countryCode, country, state }
        // Explicitly set to null when clearing (not undefined) so it's included in JSON
        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null

        // Only include countryCode when selecting a city (not when clearing)
        // When clearing city, preserve the existing country
        const countryCode = isClearing
          ? undefined
          : (cityObject?.data?.countryCode ?? cityObject?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? cityObject?.state ?? null)

        const result: Record<string, any> = {
          // API fields (mapped by patchFields)
          cityCode: cityCode,
          cityName: cityLabel,
          state: state,
          // Redux fields (direct mapping)
          [DealFields.accounts__business_city_label]: cityLabel,
          [DealFields.accounts__business_stateprovince]: state
        }

        // Only include countryCode if we're selecting a city (not clearing)
        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[DealFields.accounts__business_country_code] = countryCode
        }

        return result
      }
    },

    // State / Province
    {
      key: DealFields.accounts__business_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: formData[DealFields.accounts__business_stateprovince] || '',
      editable: true,
      autoFocus: false
    },

    // Country
    {
      key: DealFields.accounts__business_country_code,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: formData[DealFields.accounts__business_country_code] || '',
      editable: true,
      autoFocus: false,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? '')

        return {
          countryCode: countryCode,
          cityCode: null,
          state: null,
          [DealFields.accounts__business_country_label]: countryLabel,
          [DealFields.accounts__business_city_code]: null,
          [DealFields.accounts__business_city_label]: null,
          [DealFields.accounts__business_stateprovince]: null
        }
      }
    },

    // Company Description
    {
      key: DealFields.accounts__description,
      label: 'Company Description',
      type: FieldTypeEnum.TEXTAREA,
      value: formData[DealFields.accounts__description] || '',
      editable: true,
      rows: 15,
      autoFocus: false
    }
  ]
}

/**
 * Parses a name string into first_name and last_name
 * @param {string} name - Name string to parse
 * @returns {Object} Object with first_name and last_name
 */
function parseContactName(name: string): { first_name: string | null; last_name: string } {
  if (!name || typeof name !== 'string') {
    return { first_name: null, last_name: '' }
  }

  // Remove "(new)" suffix if present
  const cleanName = name.replace(/\s*\(new\)\s*$/i, '').trim()

  if (!cleanName) {
    return { first_name: null, last_name: '' }
  }

  // Split by whitespace
  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0)

  if (nameParts.length === 0) {
    return { first_name: null, last_name: '' }
  }

  if (nameParts.length === 1) {
    // Only one part - use as last_name
    return { first_name: null, last_name: nameParts[0] }
  }

  // Two or more parts - first part is first_name, rest is last_name
  return {
    first_name: nameParts[0],
    last_name: nameParts.slice(1).join(' ')
  }
}

/**
 * Checks if a contact is new (not an existing contact)
 * @param {any} contact - Contact object
 * @returns {boolean} True if contact is new
 */
function isNewContact(contact: any): boolean {
  if (!contact) return false
  return contact.value === 'new' || (contact.label && contact.label.includes('(new)'))
}

/**
 * Transforms contacts array to user_entry format for API requests
 * @param {Array} currentContacts - Current selected contacts
 * @param {Array} previousContacts - Previously selected contacts (for detecting removals)
 * @param {string} relationshipCategoryId - Relationship category ID
 * @param {boolean} isContactDeals - Whether this is for contact_deals (true) or contact_accounts (false)
 * @returns {Array} Array of user_entry objects
 */
function transformContactsToUserEntries(
  currentContacts: any[],
  previousContacts: any[],
  relationshipCategoryId: string,
  isContactDeals: boolean
): any[] {
  const ALIAS_CONTACTS = `${TABLE_CONTACTS}${FIELD_KEY_SEPARATOR}`
  const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}`
  const ALIAS_CONTACT_DEALS = `${TABLE_CONTACT_DEALS}${FIELD_KEY_SEPARATOR}`

  const FIELD_ID = `${ALIAS_CONTACTS}${ContactsModelFields.id}`
  const FIELD_EMAIL = `${ALIAS_CONTACTS}${ContactsModelFields.email}`
  const FIELD_FIRST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.first_name}`
  const FIELD_LAST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.last_name}`
  const FIELD_CONTACT_ACCOUNTS_ID = `${ALIAS_CONTACT_ACCOUNTS}${ContactAccountsModelFields.id}`
  const FIELD_CONTACT_DEALS_ID = `${ALIAS_CONTACT_DEALS}${ContactDealsModelFields.id}`
  const PROG_CONTACTS_ACTION = `${FIELD_KEY_PROG}${TABLE_CONTACTS}${FIELD_KEY_ACTION}`
  const PROG_ACTION = `${FIELD_KEY_PROG}${FIELD_KEY_ACTION}`

  // Extract contact IDs for comparison
  const extractContactId = (c: any): string | null => {
    if (!c) return null
    if (typeof c === 'object') {
      // Try value first, then id, then check if it's already an ID string
      return c.value || c.id || (typeof c === 'string' ? c : null)
    }
    if (typeof c === 'string') {
      return c
    }
    return null
  }

  const currentContactIds = new Set(
    (currentContacts || [])
      .map(extractContactId)
      .filter((id): id is string => id !== null && id !== undefined && id !== 'new')
  )
  const previousContactIds = new Set(
    (previousContacts || [])
      .map(extractContactId)
      .filter((id): id is string => id !== null && id !== undefined && id !== 'new')
  )

  const userEntries: any[] = []

  // Process current contacts (new and existing)
  ;(currentContacts || []).forEach((contact: any) => {
    const contactValue = contact && typeof contact === 'object' ? contact.value : contact
    const isNew = isNewContact(contact)

    const userEntry: any = {
      [PROG_ACTION]: ACTION_TYPE_UPSERT
    }

    if (isNew) {
      // New contact - create contact and relationship
      // Generate a proper UUID for the new contact
      const contactId = crypto.randomUUID()
      const nameSource = contact.data?.originalSearch || contact.label || ''
      const { first_name, last_name } = parseContactName(nameSource)

      userEntry[FIELD_ID] = contactId
      userEntry[FIELD_FIRST_NAME] = first_name || null
      userEntry[FIELD_LAST_NAME] = last_name
      userEntry[FIELD_EMAIL] = contact.email || null
      userEntry[PROG_CONTACTS_ACTION] = ACTION_TYPE_UPSERT
    } else {
      // Existing contact - only update relationship
      userEntry[FIELD_ID] = contactValue
      userEntry[PROG_CONTACTS_ACTION] = ''
    }

    // Set relationship ID field (check both direct property and data property)
    // Generate UUID if relationship doesn't exist yet (for new relationships)
    if (isContactDeals) {
      const relationshipId =
        contact.contact_deals_id || contact.data?.[UserEntryFields.contact_deals__id] || contact.data?.contact_deals__id
      userEntry[FIELD_CONTACT_DEALS_ID] = relationshipId || crypto.randomUUID()
    } else {
      const relationshipId =
        contact.contact_accounts_id ||
        contact.data?.[UserEntryFields.contact_accounts__id] ||
        contact.data?.contact_accounts__id
      userEntry[FIELD_CONTACT_ACCOUNTS_ID] = relationshipId || crypto.randomUUID()
    }

    userEntries.push(userEntry)
  })

  // Process removed contacts (only for existing contacts with real UUIDs, not 'new')
  previousContactIds.forEach((contactId) => {
    if (!currentContactIds.has(contactId) && contactId !== 'new' && contactId && typeof contactId === 'string') {
      // Find the previous contact by matching the ID
      const previousContact = (previousContacts || []).find((c: any) => {
        if (!c) return false
        if (typeof c === 'object') {
          return (c.value || c.id) === contactId
        }
        return c === contactId
      })

      const userEntry: any = {
        [FIELD_ID]: contactId,
        [PROG_CONTACTS_ACTION]: '',
        [PROG_ACTION]: ACTION_TYPE_DELETE
      }

      // For deletions, always include the relationship ID field (even if null)
      // The backend will handle deletion by contact_id + account_id/deal_id + relationship_category_id if ID is missing
      if (isContactDeals) {
        const relationshipId =
          previousContact?.contact_deals_id ||
          previousContact?.data?.[UserEntryFields.contact_deals__id] ||
          previousContact?.data?.contact_deals__id ||
          null
        userEntry[FIELD_CONTACT_DEALS_ID] = relationshipId
      } else {
        const relationshipId =
          previousContact?.contact_accounts_id ||
          previousContact?.data?.[UserEntryFields.contact_accounts__id] ||
          previousContact?.data?.contact_accounts__id ||
          null
        userEntry[FIELD_CONTACT_ACCOUNTS_ID] = relationshipId
      }

      userEntries.push(userEntry)
    }
  })

  return userEntries
}

/**
 * Converts contact objects to SelectListEntry format for ContactInput options
 * @param {Array} contacts - Array of contact objects with {value, label, email}
 * @returns {Array} Array of SelectListEntry objects
 */
function convertContactsToOptions(contacts: any[]): SelectListEntry[] {
  if (!contacts || !Array.isArray(contacts)) {
    return []
  }

  return contacts.map((contact) => {
    const name = contact.label || contact.name || UNKNOWN_CONTACT
    const email = contact.email || ''
    const labelWithEmail = `${name}${email && email !== '' ? ` (${email.toLowerCase()})` : ' (No Email)'}`

    return {
      value: contact.value,
      label: labelWithEmail,
      name: name
    }
  })
}

/**
 * Generates field configurations for Deal Team section
 * @param {Object} formData - Current form data
 * @param {Object} selectedContacts - Current selected contacts state
 * @param {string} companyLookupId - Company lookup ID for filtering
 * @param {Object} previousContacts - Previous selected contacts state (for detecting removals)
 * @returns {Array} Array of FieldConfig objects
 */
export function getDealTeamFields(
  formData: any,
  selectedContacts: any,
  companyLookupId: string | null,
  previousContacts?: any
): FieldConfig[] {
  // Convert selectedContacts arrays to SelectListEntry options format
  const introducedByOptions = convertContactsToOptions(selectedContacts.introduced_by || [])
  const sourcedByOptions = convertContactsToOptions(selectedContacts.sourced_by || [])
  const leadOptions = convertContactsToOptions(selectedContacts.deal_team_lead || [])

  // Store previous values for comparison
  // Use previousContacts if provided, otherwise use empty arrays to avoid false positives
  const prevContacts = previousContacts || {
    introduced_by: [],
    sourced_by: [],
    deal_team_lead: []
  }

  return [
    // Introduced By
    {
      key: DealFields.contact_accounts__introduced_by,
      label: 'Introduced by',
      labelTooltip: introducedByHelp('company'),
      type: FieldTypeEnum.CONTACT,
      value: selectedContacts.introduced_by || [],
      editable: true,
      mode: 'multiple',
      options: introducedByOptions
    },

    // Sourced By
    {
      key: DealFields.contact_accounts__sourced_by,
      label: 'Sourced by',
      labelTooltip: SOURCED_BY_HELP,
      type: FieldTypeEnum.CONTACT,
      value: selectedContacts.sourced_by || [],
      editable: true,
      mode: 'multiple',
      companyId: companyLookupId,
      options: sourcedByOptions
    },

    // Lead
    {
      key: DealFields.contact_deals__lead,
      label: 'Lead',
      labelTooltip: leadHelp('deal'),
      type: FieldTypeEnum.CONTACT,
      value: selectedContacts.deal_team_lead || [],
      editable: true,
      mode: 'multiple',
      companyId: companyLookupId,
      options: leadOptions,
      patchEndpoint: 'contact_deals',
      valueTransformer: (value: any, currentRecord: any) => {
        const currentContacts = Array.isArray(value) ? value : []
        // Get previous contacts from currentRecord (original value) or fallback to prevContacts
        const previousContacts = currentRecord?.[DealFields.contact_deals__lead] || prevContacts.deal_team_lead || []
        const userFields = transformContactsToUserEntries(
          currentContacts,
          previousContacts,
          RELATIONSHIP_CATEGORY_LEAD,
          true
        )
        const dealId =
          formData?.[DealFields.deals__id] ||
          formData?.id ||
          currentRecord?.[DealFields.deals__id] ||
          currentRecord?.id ||
          null
        return {
          id: dealId,
          userFields,
          relationship_category_id: RELATIONSHIP_CATEGORY_LEAD
        }
      }
    }
  ]
}

/**
 * Get select configuration for a given field key
 * Centralizes option retrieval logic similar to inputManagerMapper.js
 * @param {string} fieldKey - The DealFields key to get config for
 * @param {Object} options - Options object containing pipelineStageOptions, fundingStageOptions, currencyOptions
 * @returns {Object} Select configuration object with selectType, options, and/or lookupCategory
 */
function getSelectConfig(fieldKey: string, options: any): any {
  const { pipelineStageOptions, fundingStageOptions, currencyOptions } = options

  switch (fieldKey) {
    case DealFields.pipeline_deals__stage:
      return {
        selectType: SelectTypeEnum.FILTER,
        options: pipelineStageOptions || []
      }
    case DealFields.rounds__funding_stage:
      return {
        selectType: SelectTypeEnum.LOOKUP,
        lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE,
        options: fundingStageOptions || []
      }
    case DealFields.deals__currency:
      return {
        selectType: SelectTypeEnum.FILTER,
        options: currencyOptions || []
      }
    default:
      return {
        selectType: null,
        options: []
      }
  }
}

/**
 * Generates field configurations for Overview Deal Information section (used in DealOverviewSection)
 * Handles field name differences: pipeline_stage_id instead of pipeline_stage
 * @param {Object} data - Current form data or deal object
 * @param {Object} options - Options object containing pipelineStageOptions, fundingStageOptions, currencyOptions, getCurrencyById
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getOverviewDealInfoFields(
  data: any,
  options: any,
  isReadOnly = false,
  isDrawer = false
): FieldConfig[] {
  const fields: FieldConfig[] = [
    // Deal Name
    {
      key: DealFields.deals__name,
      label: 'Deal Name',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.deals__name],
      required: true,
      editable: !isReadOnly
    },

    // Company Name (hyperlinked to open company, editable)
    {
      key: DealFields.accounts__name,
      label: 'Company',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__name] || data.company_name || '',
      editable: !isReadOnly,
      hyperlinked: true,
      hyperlinkModule: 'companies',
      // Account fields need to be patched to accounts endpoint with account ID
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        // Get account ID from deals__account or accounts__id
        const accountId =
          data?.[DealFields.deals__account] ||
          data?.[DealFields.accounts__id] ||
          currentRecord?.[DealFields.deals__account] ||
          currentRecord?.[DealFields.accounts__id] ||
          data?.account_id ||
          null
        const result: any = {
          name: value,
          [DealFields.accounts__name]: value
        }
        // Only include id if we have a valid accountId
        if (accountId) {
          result.id = accountId
        }
        return result
      },
      getRecordId: () => {
        // Get company ID from deals__account or accounts__id
        return data[DealFields.deals__account] || data[DealFields.accounts__id] || data.account_id || null
      }
    } as TextFieldConfig,

    // Industry — drawer only, directly under Company (owner instruction 2026-09-16)
    ...(isDrawer
      ? [
          buildOverviewIndustryField(data, options, isReadOnly),
          buildOverviewCriticalTechnologyAreaField(data, options, isReadOnly)
        ]
      : []),

    // Deal Type — drives execution-pipeline routing (cfg.deal_execution_pipeline_map)
    {
      key: DealFields.deals__deal_type,
      label: 'Deal Type',
      type: FieldTypeEnum.SELECT,
      value: data[DealFields.deals__deal_type],
      editable: !isReadOnly,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_DEAL_TYPE,
      allowClear: false,
      confirmPatch: options.confirmDealTypeChange,
      patchEndpoint: 'deals',
      valueTransformer: (value, currentRecord) => {
        // Include deal ID from data or currentRecord to override patchId
        const dealId =
          data?.[DealFields.deals__id] || data?.id || currentRecord?.[DealFields.deals__id] || currentRecord?.id
        const result: any = {
          deal_type: value,
          [DealFields.deals__deal_type]: value
        }
        // Keep the grid label fresh in Redux (double-underscore keys are stripped from the API body)
        const label = (options.dealTypeOptions || []).find((option: any) => option.value === value)?.label
        if (label) {
          result[`${DealFields.deals__deal_type}__label`] = label
        }
        // Only include id if we have a valid dealId
        if (dealId) {
          result.id = dealId
        }
        return result
      }
    } as SelectFieldConfig,

    // Funding Stage — in the drawer it lives in the round column instead
    ...(isDrawer ? [] : [buildOverviewFundingStageField(data, options, isReadOnly)]),

    // Pipeline Stage — omitted for a deal type excluded from Deal Origination.
    // Such a deal's read returns its execution row, whose stage the
    // origination-only option list cannot label (it rendered the raw config
    // uuid), and a patch from here would aim an origination stage at that row.
    // The Execution tab owns the execution stage.
    ...(options.isOriginationExcluded?.(data[DealFields.deals__deal_type])
      ? []
      : [buildOverviewPipelineStageField(data, options, isReadOnly)])
  ]

  return fields
}

function buildOverviewPipelineStageField(data: any, options: any, isReadOnly: boolean): FieldConfig {
  return {
    key: DealFields.pipeline_deals__stage,
    label: 'Pipeline Stage',
    type: FieldTypeEnum.SELECT,
    value: data[DealFields.pipeline_deals__stage],
    editable: !isReadOnly,
    ...getSelectConfig(DealFields.pipeline_deals__stage, options),
    allowClear: true,
    // Commit gate: moving into an origination Committed stage without a
    // commitment / security type / valuation prompts first (useCommitDealGate)
    confirmPatch: options.confirmStageChange,
    // Configure to patch pipeline-deals endpoint (not pipeline_deals)
    patchEndpoint: 'pipeline-deals',
    patchFields: [
      { sourceKey: 'value', targetField: 'stage' },
      { sourceKey: 'stageDate', targetField: 'stage_date' }
    ],
    // Transform value to update both API fields and Redux fields
    valueTransformer: (value, currentRecord) => {
      // Include pipeline_deals__id from data or currentRecord to override patchId
      const pipelineDealsId =
        data?.[DealFields.pipeline_deals__id] ||
        currentRecord?.[DealFields.pipeline_deals__id] ||
        data?.['pipeline_deals__id'] ||
        currentRecord?.['pipeline_deals__id']
      const stageDate = new Date().toISOString().split('T')[0]
      const result: any = {
        // API fields (mapped by patchFields)
        value: value, // Will be mapped to 'stage'
        stageDate: stageDate, // Will be mapped to 'stage_date'
        // Redux fields (direct mapping)
        [DealFields.pipeline_deals__stage]: value,
        [DealFields.pipeline_deals__stage_date]: stageDate,
        [DealFields.pipeline_config__id]: value // Same as pipeline_deals__stage, needed for Kanban board column assignment
      }
      // Only include id if we have a valid pipelineDealsId
      if (pipelineDealsId) {
        result.id = pipelineDealsId
      }
      return result
    }
  } as SelectFieldConfig
}

/** Industry (accounts patch, cfg.industries lookup) — the drawer's deal column, under Company. */
export function buildOverviewIndustryField(data: any, options: any, isReadOnly = false): FieldConfig {
  return {
    key: DealFields.accounts__industry,
    label: 'Industry',
    type: FieldTypeEnum.SELECT,
    value: data[DealFields.accounts__industry],
    editable: !isReadOnly,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_INDUSTRY,
    allowClear: true,
    patchEndpoint: 'accounts',
    valueTransformer: (value, currentRecord) => {
      const accountId =
        data?.[DealFields.deals__account] ||
        data?.[DealFields.accounts__id] ||
        currentRecord?.[DealFields.deals__account] ||
        currentRecord?.[DealFields.accounts__id] ||
        null
      const result: any = { industry: value, [DealFields.accounts__industry]: value }
      const label = (options.getLookupOptions?.(LOOKUP_CATEGORY_INDUSTRY) || []).find(
        (option: any) => option.value === value
      )?.label
      result[DealFields.accounts__industry__label] = label || null
      if (accountId) result.id = accountId
      return result
    }
  } as SelectFieldConfig
}

/** Critical Technology Area (accounts patch) — the drawer's deal column, directly
 *  below Industry. Same shape as buildOverviewIndustryField: the field lives on
 *  the DEAL form but writes to the deal's ACCOUNT, so the transformer has to name
 *  the account id explicitly — the patch route would otherwise target the deal. */
export function buildOverviewCriticalTechnologyAreaField(data: any, options: any, isReadOnly = false): FieldConfig {
  const ctaOptions = options.getLookupOptions?.(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA) || []
  return buildCriticalTechnologyAreaField({
    options: ctaOptions,
    value: data[DealFields.accounts__critical_technology_area],
    key: DealFields.accounts__critical_technology_area,
    editable: !isReadOnly,
    patchEndpoint: 'accounts',
    valueTransformer: (value: any, currentRecord: any) => {
      const accountId =
        data?.[DealFields.deals__account] ||
        data?.[DealFields.accounts__id] ||
        currentRecord?.[DealFields.deals__account] ||
        currentRecord?.[DealFields.accounts__id] ||
        null
      const next = !value || (typeof value === 'string' && value.trim() === '') ? null : value
      const result: any = {
        [CRITICAL_TECHNOLOGY_AREA_COLUMN]: next,
        [DealFields.accounts__critical_technology_area]: next
      }
      result[DealFields.accounts__critical_technology_area__label] =
        ctaOptions.find((option: any) => option.value === next)?.label || null
      if (accountId) result.id = accountId
      return result
    }
  })
}

/** Funding Stage (rounds patch) — shared by the overview deal column and the drawer's round column. */
export function buildOverviewFundingStageField(data: any, options: any, isReadOnly = false): FieldConfig {
  const roundId = data?.[DealFields.rounds__id] || data?.round || data?.['rounds__id'] || null
  return {
    key: DealFields.rounds__funding_stage,
    label: 'Funding Stage',
    type: FieldTypeEnum.SELECT,
    value: data[DealFields.rounds__funding_stage],
    editable: !isReadOnly && !!roundId,
    ...getSelectConfig(DealFields.rounds__funding_stage, options),
    allowClear: true,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      // Include round ID from data or currentRecord to override patchId
      const roundId =
        data?.[DealFields.rounds__id] ||
        data?.round ||
        currentRecord?.[DealFields.rounds__id] ||
        currentRecord?.round ||
        currentRecord?.['rounds__id'] ||
        data?.['rounds__id']
      const result: any = {
        funding_stage: value,
        [DealFields.rounds__funding_stage]: value
      }
      // Only include id if we have a valid roundId
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  }
}

const overviewRoundId = (data: any, currentRecord: any) =>
  data?.[DealFields.rounds__id] ||
  data?.round ||
  currentRecord?.[DealFields.rounds__id] ||
  currentRecord?.round ||
  currentRecord?.['rounds__id'] ||
  data?.['rounds__id']

/** Round Name (rounds patch, hyperlinked to the round) — the drawer's round column. */
export function buildOverviewRoundNameField(data: any, isReadOnly = false): FieldConfig {
  const roundId = data?.[DealFields.rounds__id] || data?.round || data?.['rounds__id'] || null
  return {
    key: DealFields.rounds__name,
    label: 'Round Name',
    type: FieldTypeEnum.TEXT,
    value: data[DealFields.rounds__name],
    editable: !isReadOnly && !!roundId,
    hyperlinked: true,
    hyperlinkModule: 'rounds',
    getRecordId: () => data?.[DealFields.rounds__id] || data?.round || null,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = { name: value, [DealFields.rounds__name]: value }
      const id = overviewRoundId(data, currentRecord)
      if (id) result.id = id
      return result
    }
  } as TextFieldConfig
}

/** Security Type (rounds patch) — the drawer's round column. */
export function buildOverviewSecurityTypeField(data: any, options: any, isReadOnly = false): FieldConfig {
  const roundId = data?.[DealFields.rounds__id] || data?.round || data?.['rounds__id'] || null
  return {
    key: DealFields.rounds__security_type,
    label: 'Security Type',
    type: FieldTypeEnum.SELECT,
    value: data[DealFields.rounds__security_type],
    editable: !isReadOnly && !!roundId,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE,
    allowClear: true,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = { security_type: value, [DealFields.rounds__security_type]: value }
      const label = (options.getLookupOptions?.(LOOKUP_CATEGORY_SECURITY_TYPE) || []).find(
        (option: any) => option.value === value
      )?.label
      if (label) result[DealFields.rounds__security_type__label] = label
      const id = overviewRoundId(data, currentRecord)
      if (id) result.id = id
      return result
    }
  } as SelectFieldConfig
}

/** Allocation Available (deals patch) — shared by the overview and Detail tab round sections. */
export function buildOverviewAllocationField(data: any, options: any, isReadOnly = false): FieldConfig {
  const { getCurrencyById } = options
  return {
    key: DealFields.deals__allocation_available,
    label: 'Allocation Available',
    type: FieldTypeEnum.CURRENCY,
    value: data[DealFields.deals__allocation_available],
    editable: !isReadOnly,
    currencySymbol: getCurrencyById(data[DealFields.deals__currency] || data.currency)?.symbol || '$',
    min: 0,
    step: 0.01,
    patchEndpoint: 'deals',
    valueTransformer: (value, currentRecord) => {
      // Include deal ID from data or currentRecord to override patchId
      const dealId =
        data?.[DealFields.deals__id] || data?.id || currentRecord?.[DealFields.deals__id] || currentRecord?.id
      const result: any = {
        allocation_available: value,
        [DealFields.deals__allocation_available]: value
      }
      // Only include id if we have a valid dealId
      if (dealId) {
        result.id = dealId
      }
      return result
    }
  } as NumberFieldConfig
}

/**
 * Generates field configurations for Overview Round Information section (used in DealOverviewSection)
 * Handles field name differences: raise_amount instead of raising_amount
 * @param {Object} data - Current form data or deal object
 * @param {Object} options - Options object containing pipelineStageOptions, fundingStageOptions, currencyOptions, getCurrencyById
 * @param {boolean} showCurrency - Whether to include currency field
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getOverviewRoundInfoFields(
  data: any,
  options: any,
  showCurrency = false,
  isReadOnly = false,
  isDrawer = false
): FieldConfig[] {
  const { getCurrencyById } = options
  const roundId = data?.[DealFields.rounds__id] || data?.round || data?.['rounds__id'] || null

  const fields: FieldConfig[] = []

  // Drawer order (owner instruction 2026-09-16): Round Name, Funding Stage,
  // Security Type, Raising, Allocation Available, Pre-money, Post-money. The
  // Dashboard tab keeps its original order below.
  if (isDrawer) {
    fields.push(buildOverviewRoundNameField(data, isReadOnly))
    fields.push(buildOverviewFundingStageField(data, options, isReadOnly))
    fields.push(buildOverviewSecurityTypeField(data, options, isReadOnly))
  }

  // Currency - only show in full form (single-column mode)
  if (showCurrency) {
    fields.push({
      key: DealFields.deals__currency,
      label: 'Currency',
      type: FieldTypeEnum.SELECT,
      value: data[DealFields.deals__currency],
      required: true,
      editable: !isReadOnly,
      ...getSelectConfig(DealFields.deals__currency, options),
      allowClear: true
    })
  }

  // Raising Amount (using raise_amount key instead of raising_amount)
  fields.push({
    key: DealFields.rounds__raise_amount,
    label: 'Raising',
    type: FieldTypeEnum.CURRENCY,
    value: data[DealFields.rounds__raise_amount],
    editable: !isReadOnly && !!roundId,
    currencySymbol: getCurrencyById(data[DealFields.deals__currency])?.symbol || '$',
    min: 0,
    step: 0.01,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      // Include round ID from data or currentRecord to override patchId
      // Try multiple field name variations since DealFields.rounds__id might be the full key
      const roundId =
        data?.[DealFields.rounds__id] ||
        data?.round ||
        currentRecord?.[DealFields.rounds__id] ||
        currentRecord?.round ||
        currentRecord?.['rounds__id'] ||
        data?.['rounds__id']
      const result: any = {
        raise_amount: value,
        [DealFields.rounds__raise_amount]: value
      }
      // Only include id if we have a valid roundId (not undefined/null)
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  if (isDrawer) {
    fields.push(buildOverviewAllocationField(data, options, isReadOnly))
  }

  // Pre-money Valuation
  fields.push({
    key: DealFields.rounds__pre_money_valuation,
    label: 'Pre-money Valuation',
    type: FieldTypeEnum.CURRENCY,
    value: data[DealFields.rounds__pre_money_valuation],
    editable: !isReadOnly && !!roundId,
    currencySymbol: getCurrencyById(data[DealFields.deals__currency])?.symbol || '$',
    min: 0,
    step: 0.01,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      // Include round ID from data or currentRecord to override patchId
      const roundId =
        data?.[DealFields.rounds__id] ||
        data?.round ||
        currentRecord?.[DealFields.rounds__id] ||
        currentRecord?.round ||
        currentRecord?.['rounds__id'] ||
        data?.['rounds__id']
      const preMoneyValue = value !== null && value !== undefined ? value : null
      const postMoneyValue =
        currentRecord?.[DealFields.rounds__post_money_valuation] !== null &&
        currentRecord?.[DealFields.rounds__post_money_valuation] !== undefined
          ? currentRecord[DealFields.rounds__post_money_valuation]
          : data?.[DealFields.rounds__post_money_valuation] !== null &&
              data?.[DealFields.rounds__post_money_valuation] !== undefined
            ? data[DealFields.rounds__post_money_valuation]
            : null
      const result: any = {
        pre_money_valuation: preMoneyValue,
        post_money_valuation: postMoneyValue,
        [DealFields.rounds__pre_money_valuation]: preMoneyValue,
        [DealFields.rounds__post_money_valuation]: postMoneyValue
      }
      // Only include id if we have a valid roundId
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Post-money Valuation
  fields.push({
    key: DealFields.rounds__post_money_valuation,
    label: 'Post-money Valuation',
    type: FieldTypeEnum.CURRENCY,
    value: data[DealFields.rounds__post_money_valuation],
    editable: !isReadOnly && !!roundId,
    currencySymbol: getCurrencyById(data[DealFields.deals__currency])?.symbol || '$',
    min: 0,
    step: 0.01,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      // Include round ID from data or currentRecord to override patchId
      const roundId =
        data?.[DealFields.rounds__id] ||
        data?.round ||
        currentRecord?.[DealFields.rounds__id] ||
        currentRecord?.round ||
        currentRecord?.['rounds__id'] ||
        data?.['rounds__id']
      const postMoneyValue = value !== null && value !== undefined ? value : null
      const preMoneyValue =
        currentRecord?.[DealFields.rounds__pre_money_valuation] !== null &&
        currentRecord?.[DealFields.rounds__pre_money_valuation] !== undefined
          ? currentRecord[DealFields.rounds__pre_money_valuation]
          : data?.[DealFields.rounds__pre_money_valuation] !== null &&
              data?.[DealFields.rounds__pre_money_valuation] !== undefined
            ? data[DealFields.rounds__pre_money_valuation]
            : null
      const result: any = {
        pre_money_valuation: preMoneyValue,
        post_money_valuation: postMoneyValue,
        [DealFields.rounds__pre_money_valuation]: preMoneyValue,
        [DealFields.rounds__post_money_valuation]: postMoneyValue
      }
      // Only include id if we have a valid roundId
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Allocation Available closes the Dashboard list; in the drawer it sits under Raising
  if (!isDrawer) {
    fields.push(buildOverviewAllocationField(data, options, isReadOnly))
  }

  return fields
}

/**
 * Generates field configurations for Company section (used in DetailTab)
 * @param {Object} data - Current form data or deal object
 * @param {Object} options - Options object containing getCompanyDetailsById, getLookupOptions, etc.
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getCompanyFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  const { getCompanyDetailsById, getLookupOptions } = options
  const industryOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_INDUSTRY) : []
  const ctaOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA) : []

  return [
    // Company Name (TEXT type for direct editing, hyperlinked to open company)
    {
      key: DealFields.accounts__name,
      label: 'Name',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__name] || data.company_name || '',
      editable: !isReadOnly,
      hyperlinked: true,
      hyperlinkModule: 'companies',
      getRecordId: () => {
        // Get company ID from deals__account or accounts__id
        return data[DealFields.deals__account] || data[DealFields.accounts__id] || data.account_id || null
      }
    } as TextFieldConfig,
    // Legal Name
    {
      key: DealFields.accounts__legal_name,
      label: 'Legal name',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__legal_name] || data.company_legal_name || '',
      editable: !isReadOnly
    },
    // Industry
    {
      key: DealFields.accounts__industry,
      label: 'Industry',
      type: FieldTypeEnum.SELECT,
      value: data[DealFields.accounts__industry] || data.company_industry || '',
      editable: !isReadOnly,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_INDUSTRY,
      options: industryOptions,
      allowClear: true
    },
    // Critical Technology Area — immediately below Industry.
    buildCriticalTechnologyAreaField({
      options: ctaOptions,
      value: data[DealFields.accounts__critical_technology_area],
      key: DealFields.accounts__critical_technology_area,
      editable: !isReadOnly
    }),
    // Website
    {
      key: DealFields.accounts__website,
      label: 'Website',
      type: FieldTypeEnum.URL,
      value: data[DealFields.accounts__website] || data.company_website || '',
      editable: !isReadOnly
    },
    // Description
    {
      key: DealFields.accounts__description,
      label: 'Description',
      type: FieldTypeEnum.TEXTAREA,
      value: data[DealFields.accounts__description] || data.company_description || '',
      editable: !isReadOnly,
      rows: 15
    }
  ]
}

/**
 * Generates field configurations for Round Information section (used in DetailTab)
 * @param {Object} data - Current form data or deal object
 * @param {Object} options - Options object containing getCurrencyById, getCurrencyOptions, getLookupOptions
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getRoundInformationFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  const { getCurrencyById, getCurrencyOptions, getLookupOptions } = options
  const fundingStageOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_FUNDING_STAGE) : []
  const securityTypeOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_SECURITY_TYPE) : []
  const currencyOptions = getCurrencyOptions ? getCurrencyOptions() : []
  const roundId = data[DealFields.rounds__id] || data.round || null

  // Order is the owner's (2026-09-16): Round Name, Funding Stage, Security
  // Type, Amount Raising, Allocation Available, Pre-money, Post-money, Currency.

  return [
    // Round Name
    {
      key: DealFields.rounds__name,
      label: 'Round Name',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.rounds__name] || data.round_name || '',
      editable: !isReadOnly && !!roundId,
      patchEndpoint: 'rounds',
      hyperlinked: true,
      hyperlinkModule: 'rounds',
      getRecordId: (value, record) => {
        // Extract round ID from the data passed to getRoundInformationFields
        return data[DealFields.rounds__id] || data.round || null
      },
      valueTransformer: (value) => {
        // Don't include id - let patchId from InputManager handle it
        return {
          name: value,
          [DealFields.rounds__name]: value
        }
      }
    },
    // Funding Stage
    {
      key: DealFields.rounds__funding_stage,
      label: 'Funding Stage',
      type: FieldTypeEnum.SELECT,
      value: data[DealFields.rounds__funding_stage] || data.funding_stage || '',
      editable: !isReadOnly && !!roundId,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE,
      options: fundingStageOptions,
      allowClear: true,
      patchEndpoint: 'rounds',
      valueTransformer: (value, currentRecord) => {
        // Don't include id - let patchId from InputManager handle it (set correctly in RoundInformationSection)
        return {
          funding_stage: value,
          [DealFields.rounds__funding_stage]: value
        }
      }
    },
    // Security Type
    {
      key: DealFields.rounds__security_type,
      label: 'Security Type',
      type: FieldTypeEnum.SELECT,
      value: data[DealFields.rounds__security_type] || data.security_type || '',
      editable: !isReadOnly && !!roundId,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE,
      options: securityTypeOptions,
      allowClear: true,
      patchEndpoint: 'rounds',
      valueTransformer: (value) => {
        // Don't include id - let patchId from InputManager handle it
        return {
          security_type: value,
          [DealFields.rounds__security_type]: value
        }
      }
    },
    // Raise Amount (CURRENCY using DealFields.rounds__raise_amount)
    {
      key: DealFields.rounds__raise_amount,
      label: 'Amount Raising',
      type: FieldTypeEnum.CURRENCY,
      value: data[DealFields.rounds__raise_amount] || data.raise_amount || '',
      editable: !isReadOnly && !!roundId,
      currencySymbol: getCurrencyById(data[DealFields.deals__currency] || data.currency)?.symbol || '$',
      min: 0,
      step: 0.01,
      patchEndpoint: 'rounds',
      valueTransformer: (value) => {
        // Don't include id - let patchId from InputManager handle it
        return {
          raise_amount: value,
          [DealFields.rounds__raise_amount]: value
        }
      }
    },
    // Allocation Available — a deals column; patches the deal, not the round
    buildOverviewAllocationField(data, options, isReadOnly),
    // Pre-money Valuation
    {
      key: DealFields.rounds__pre_money_valuation,
      label: 'Pre-money Valuation',
      type: FieldTypeEnum.CURRENCY,
      value: data[DealFields.rounds__pre_money_valuation] || data.pre_money_valuation || '',
      editable: !isReadOnly && !!roundId,
      currencySymbol: getCurrencyById(data[DealFields.deals__currency] || data.currency)?.symbol || '$',
      min: 0,
      step: 0.01,
      patchEndpoint: 'rounds',
      valueTransformer: (value) => {
        // Don't include id - let patchId from InputManager handle it
        return {
          pre_money_valuation: value,
          [DealFields.rounds__pre_money_valuation]: value
        }
      }
    },
    // Post-money Valuation
    {
      key: DealFields.rounds__post_money_valuation,
      label: 'Post-money Valuation',
      type: FieldTypeEnum.CURRENCY,
      value: data[DealFields.rounds__post_money_valuation] || data.post_money_valuation || '',
      editable: !isReadOnly && !!roundId,
      currencySymbol: getCurrencyById(data[DealFields.deals__currency] || data.currency)?.symbol || '$',
      min: 0,
      step: 0.01,
      patchEndpoint: 'rounds',
      valueTransformer: (value) => {
        // Don't include id - let patchId from InputManager handle it
        return {
          post_money_valuation: value,
          [DealFields.rounds__post_money_valuation]: value
        }
      }
    },
    // Currency
    {
      key: DealFields.deals__currency,
      label: 'Currency',
      type: FieldTypeEnum.SELECT,
      value: data[DealFields.deals__currency] || data.currency || '',
      editable: !isReadOnly,
      selectType: SelectTypeEnum.FILTER,
      options: currencyOptions,
      allowClear: true
    } as SelectFieldConfig
  ]
}

/**
 * Generates field configurations for Business Address section (used in DetailTab)
 * @param {Object} data - Current form data or deal object
 * @param {Object} options - Options object
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getBusinessAddressFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  return [
    // Street address 1
    {
      key: DealFields.accounts__business_address_1,
      label: 'Street address 1',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__business_address_1] || data.company_street_1 || '',
      editable: !isReadOnly
    },
    // Street address 2
    {
      key: DealFields.accounts__business_address_2,
      label: 'Street address 2',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__business_address_2] || data.company_street_2 || '',
      editable: !isReadOnly
    },
    // City
    {
      key: DealFields.accounts__business_city_label,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: data[DealFields.accounts__business_city_label] || data.company_city || '',
      editable: !isReadOnly,
      country: data[DealFields.accounts__business_country_code] || data.company_country_code || data.company_country,
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'cityName', targetField: 'business_city_label' },
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject, currentRecord) => {
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? null) || null

        const result: Record<string, any> = {
          cityCode,
          cityName: cityLabel,
          state,
          [DealFields.accounts__business_city_label]: cityLabel,
          [DealFields.accounts__business_stateprovince]: state
        }

        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[DealFields.accounts__business_country_code] = countryCode
        }

        return result
      }
    },
    // State/Province
    {
      key: DealFields.accounts__business_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__business_stateprovince] || data.company_state || '',
      editable: !isReadOnly
    },
    // ZIP code
    {
      key: DealFields.accounts__business_postalcode,
      label: 'ZIP code',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__business_postalcode] || data.company_zip || '',
      editable: !isReadOnly
    },
    // Country
    {
      key: DealFields.accounts__business_country_label,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: data[DealFields.accounts__business_country_label] || data.company_country || '',
      editable: !isReadOnly,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          countryCode,
          cityCode: null,
          state: null,
          [DealFields.accounts__business_country_code]: countryCode,
          [DealFields.accounts__business_country_label]: countryLabel,
          [DealFields.accounts__business_city_label]: null,
          [DealFields.accounts__business_stateprovince]: null
        }
      }
    }
  ]
}

/**
 * Generates field configurations for Registered Office section (used in DetailTab)
 * @param {Object} data - Current form data or deal object
 * @param {Object} options - Options object
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getRegisteredOfficeFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  return [
    // Street address 1
    {
      key: DealFields.accounts__office_address_1,
      label: 'Street address 1',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__office_address_1] || data.registered_street_1 || '',
      editable: !isReadOnly
    },
    // Street address 2
    {
      key: DealFields.accounts__office_address_2,
      label: 'Street address 2',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__office_address_2] || data.registered_street_2 || '',
      editable: !isReadOnly
    },
    // City
    {
      key: DealFields.accounts__office_city_code__label,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: data[DealFields.accounts__office_city_code__label] || data.registered_city || '',
      editable: !isReadOnly,
      country:
        data[DealFields.accounts__office_country_code] || data.registered_country_code || data.registered_country,
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'office_city_code' },
        { sourceKey: 'countryCode', targetField: 'office_country_code' },
        { sourceKey: 'state', targetField: 'office_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject) => {
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? null) || null

        const result: Record<string, any> = {
          cityCode,
          state,
          [DealFields.accounts__office_city_code__label]: cityLabel,
          [DealFields.accounts__office_stateprovince]: state
        }

        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[DealFields.accounts__office_country_code] = countryCode
        }

        return result
      }
    },
    // State/Province
    {
      key: DealFields.accounts__office_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__office_stateprovince] || data.registered_state || '',
      editable: !isReadOnly
    },
    // ZIP code
    {
      key: DealFields.accounts__office_postalcode,
      label: 'ZIP code',
      type: FieldTypeEnum.TEXT,
      value: data[DealFields.accounts__office_postalcode] || data.registered_zip || '',
      editable: !isReadOnly
    },
    // Country
    {
      key: DealFields.accounts__office_country_code__label,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: data[DealFields.accounts__office_country_code__label] || data.registered_country || '',
      editable: !isReadOnly,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'office_country_code' },
        { sourceKey: 'cityCode', targetField: 'office_city_code' },
        { sourceKey: 'state', targetField: 'office_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          countryCode,
          cityCode: null,
          state: null,
          [DealFields.accounts__office_country_code]: countryCode,
          [DealFields.accounts__office_country_code__label]: countryLabel,
          [DealFields.accounts__office_city_code__label]: null,
          [DealFields.accounts__office_stateprovince]: null
        }
      }
    }
  ]
}
